import type { U3141ChildLotChain } from '../U3141Service';
import { u3142IsPreRenovationLotNumber } from '../../u3142Lot/tools/u3142IsPreRenovationLotNumber';

export function u3141NormalizeGraphLotState(lotState: string | undefined | null): 'validate' | 'notValidate' | undefined {
  if (lotState == null || lotState === '') return undefined;
  return lotState === 'validate' ? 'validate' : 'notValidate';
}

/** Verrouillage auto (sans clic cadenas) — fiche org validée uniquement. */
export function u3141GraphLotLockCanApply(lotState: string | undefined | null): boolean {
  return u3141NormalizeGraphLotState(lotState) === 'validate';
}

function hasNonEmptyString(value: string | undefined | null): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * Fiche « complète » pour recette / messages (district + cadastre si lot origine).
 * N’empêche pas le cadenas : une fiche org validée reste verrouillée même si des champs sont vides (BL-0695).
 */
export function u3141GraphLotRequiredFieldsComplete(child: U3141ChildLotChain | null | undefined): boolean {
  const ld = child?.lotDetail;
  if (!ld) return false;
  if (!hasNonEmptyString(ld.lot)) return false;

  if (u3142IsPreRenovationLotNumber(ld.lot)) {
    const districtOk = hasNonEmptyString(ld.district?.propertyDistrict) || hasNonEmptyString(ld.district?.id);
    const cadastreOk = hasNonEmptyString(ld.cadastre?.cadastre) || hasNonEmptyString(ld.cadastre?.id);
    if (!districtOk || !cadastreOk) return false;
  }

  return true;
}

/** Date d’établissement présente (fermeture du cadenas — BL-0697). */
export function u3141GraphLotHasEstablishmentDate(value: string | null | undefined): boolean {
  return hasNonEmptyString(value);
}

/** District et cadastre présents (fermeture du cadenas — BL-0697). */
export function u3141GraphLotHasDistrictAndCadastreForLockClose(
  lotDetail: U3141ChildLotChain['lotDetail'] | null | undefined,
): boolean {
  if (!lotDetail) return false;
  const districtOk =
    hasNonEmptyString(lotDetail.district?.propertyDistrict) || hasNonEmptyString(lotDetail.district?.id);
  const cadastreOk =
    hasNonEmptyString(lotDetail.cadastre?.cadastre) || hasNonEmptyString(lotDetail.cadastre?.id);
  return districtOk && cadastreOk;
}

/** Type d’opération présent (fermeture du cadenas — BL-0697). */
export function u3141GraphLotHasOperationTypeForLockClose(
  lotDetail: U3141ChildLotChain['lotDetail'] | null | undefined,
): boolean {
  if (!lotDetail) return false;
  return hasNonEmptyString(lotDetail.operationType);
}

/** District, cadastre, date d’établissement et type d’opération requis pour fermer le cadenas (BL-0697). */
export function u3141GraphLotCanCloseLock(
  lotDetail: U3141ChildLotChain['lotDetail'] | null | undefined,
): boolean {
  if (!lotDetail) return false;
  return (
    u3141GraphLotHasDistrictAndCadastreForLockClose(lotDetail) &&
    u3141GraphLotHasEstablishmentDate(lotDetail.dateEstablishment) &&
    u3141GraphLotHasOperationTypeForLockClose(lotDetail)
  );
}

/**
 * Fermer le cadenas exige district, cadastre, date d’établissement et type d’opération.
 * Un cadenas déjà fermé reste cliquable pour rouvrir l’édition.
 */
export function u3141GraphLotLockCloseDisabled(
  fieldsLocked: boolean,
  lotDetail: U3141ChildLotChain['lotDetail'] | null | undefined,
): boolean {
  return !fieldsLocked && !u3141GraphLotCanCloseLock(lotDetail);
}

/** Verrouillage initial (sans clic cadenas) : fiche org validée → lecture seule, champs optionnels ou non (BL-0695). */
export function u3141GraphLotDefaultFieldsLocked(
  lotState: string | undefined | null,
  _child: U3141ChildLotChain | null | undefined,
): boolean {
  return u3141GraphLotLockCanApply(lotState);
}
